import { getThreadRange } from "@tiptap-pro/extension-comments";
import type { Suggestion } from "@tiptap-pro/extension-tracked-changes";
import { getSuggestionRange } from "@tiptap-pro/extension-tracked-changes";
import type { Editor } from "@tiptap/react";
import {
  getVisibleTiptapThreadComments,
  isTiptapUserCommentThread,
  toTiptapCommentDate,
} from "./tiptapCommentUtils";
import type { TiptapCommentThread } from "./types";

export type TiptapCollabPanelFilter = "all" | "unresolved" | "resolved";

export type TiptapCollaborationItemKind = "comment" | "suggestion";

export type TiptapCollaborationItem =
  | {
      createdAt: Date;
      id: string;
      kind: "comment";
      position: number | null;
      thread: TiptapCommentThread;
    }
  | {
      createdAt: Date;
      id: string;
      kind: "suggestion";
      position: number | null;
      suggestion: Suggestion;
    };

export const EMPTY_TIPTAP_COMMENT_THREADS: TiptapCommentThread[] = [];
export const EMPTY_TIPTAP_SUGGESTIONS: Suggestion[] = [];

export const getTiptapCollaborationItemKey = (
  kind: TiptapCollaborationItemKind,
  id: string
) => `${kind}:${id}`;

export const isTiptapThreadUnresolved = (thread: TiptapCommentThread) =>
  !thread.resolvedAt;

const getDate = (value: Date | string | undefined) => {
  const date = value instanceof Date ? value : new Date(value ?? 0);

  return Number.isNaN(date.getTime()) ? new Date(0) : date;
};

const getFinitePosition = (value: number | undefined): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const getThreadDocumentPosition = (
  editor: Editor | null | undefined,
  thread: TiptapCommentThread
) => {
  if (!editor) {
    return null;
  }

  try {
    return getFinitePosition(getThreadRange(editor, thread.id)?.from);
  } catch {
    return null;
  }
};

const getSuggestionDocumentPosition = (
  editor: Editor | null | undefined,
  suggestion: Suggestion
) => {
  if (editor) {
    try {
      const currentPosition = getFinitePosition(
        getSuggestionRange(editor, suggestion.id)?.from
      );

      if (currentPosition !== null) {
        return currentPosition;
      }
    } catch {
      // Fall back to the suggestion snapshot below.
    }
  }

  return getFinitePosition(suggestion.from);
};

const sortCollaborationItems = (
  items: TiptapCollaborationItem[]
): TiptapCollaborationItem[] =>
  items.sort((a, b) => {
    if (a.position !== null && b.position !== null) {
      const positionDelta = a.position - b.position;

      if (positionDelta !== 0) {
        return positionDelta;
      }
    } else if (a.position !== null) {
      return -1;
    } else if (b.position !== null) {
      return 1;
    }

    return a.createdAt.getTime() - b.createdAt.getTime();
  });

export const getVisibleTiptapCollaborationThreads = (
  threads: TiptapCommentThread[]
) =>
  threads
    .filter(
      (thread) =>
        isTiptapUserCommentThread(thread) &&
        !thread.deletedAt &&
        getVisibleTiptapThreadComments(thread).length > 0
    )
    .sort(
      (a, b) =>
        toTiptapCommentDate(a.createdAt).getTime() -
        toTiptapCommentDate(b.createdAt).getTime()
    );

export const getSelectedTiptapCollaborationItemKey = ({
  selectedThreadId,
  suggestions,
  threads,
  visibleThreads,
}: {
  selectedThreadId: string | null;
  suggestions: Suggestion[];
  threads: TiptapCommentThread[];
  visibleThreads: TiptapCommentThread[];
}) => {
  if (!selectedThreadId) {
    return null;
  }

  if (visibleThreads.some((thread) => thread.id === selectedThreadId)) {
    return getTiptapCollaborationItemKey("comment", selectedThreadId);
  }

  if (suggestions.some((suggestion) => suggestion.id === selectedThreadId)) {
    return getTiptapCollaborationItemKey("suggestion", selectedThreadId);
  }

  const selectedThread = threads.find(
    (thread) => thread.id === selectedThreadId
  );
  const suggestionId = selectedThread?.data?.suggestionId;

  if (
    suggestionId &&
    suggestions.some((suggestion) => suggestion.id === suggestionId)
  ) {
    return getTiptapCollaborationItemKey("suggestion", suggestionId);
  }

  return null;
};

export const getTiptapCollaborationItems = ({
  editor,
  filter,
  suggestions,
  threads,
}: {
  editor?: Editor | null;
  filter: TiptapCollabPanelFilter;
  suggestions: Suggestion[];
  threads: TiptapCommentThread[];
}): TiptapCollaborationItem[] => {
  const items: TiptapCollaborationItem[] = [];

  for (const thread of threads) {
    if (filter === "unresolved" && !isTiptapThreadUnresolved(thread)) {
      continue;
    }

    if (filter === "resolved" && isTiptapThreadUnresolved(thread)) {
      continue;
    }

    items.push({
      createdAt: getDate(thread.createdAt),
      id: thread.id,
      kind: "comment",
      position: getThreadDocumentPosition(editor, thread),
      thread,
    });
  }

  if (filter !== "resolved") {
    for (const suggestion of suggestions) {
      items.push({
        createdAt: getDate(suggestion.createdAt),
        id: suggestion.id,
        kind: "suggestion",
        position: getSuggestionDocumentPosition(editor, suggestion),
        suggestion,
      });
    }
  }

  return sortCollaborationItems(items);
};
